import {
  Box,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import { Button } from "src/components/common/FormComponents";
import { path } from "src/constants/path";
import { dateAndTimeStamp } from "src/utils/helper";
import { getDemandNotePdf } from "src/utils/api/dashboard";
import { fileUpload, getFileByKey } from "src/utils/api/fileupload";
import {
  createPaymentQuery,
  generateDemandNote,
  getPaymentByEpid,
  verifyPayment,
} from "src/utils/api/payment";
import { adminInstance } from "src/dataProvider/axios";

// Match whatever host the API calls actually go to — the axios instance is the
// source of truth (the env var REACT_APP_ADMIN_USER_BASE_URL can point at prod
// while local dev hits 127.0.0.1, which would cause cross-host 404s).
const API_BASE_URL =
  adminInstance.defaults.baseURL || "http://127.0.0.1:3000/api/";

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  DEMAND_NOTE_PENDING: "Demand Note Pending",
  PAYMENT_PENDING: "Payment Pending",
  PAYMENT_VERIFICATION: "Payment Verification",
  PAYMENT_REJECTED: "Payment Rejected",
  PAYMENT_RECEIVED: "Payment Received",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  DEMAND_NOTE_PENDING: "#757575",
  PAYMENT_PENDING: "#1976d2",
  PAYMENT_VERIFICATION: "#ed6c02",
  PAYMENT_REJECTED: "#d32f2f",
  PAYMENT_RECEIVED: "#2e7d32",
};

const greenLinkSx = {
  color: "#36B1A0",
  fontWeight: 500,
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

const uploadDocument = async (file: File) => {
  const form = new FormData();
  form.append("file", file);
  const res = await fileUpload(form as any);
  return {
    key: res.data?.data?.uploadedFile?.key,
    name: file.name,
  };
};

const downloadDocByKey = async (docKey: string, fileName?: string) => {
  try {
    const res = await getFileByKey(docKey);
    const url =
      (res as any)?.data?.data?.fileUrl ||
      (res as any)?.data?.fileUrl;
    if (!url) {
      console.warn("getFileByKey returned no fileUrl", res?.data);
      return;
    }
    // Use an anchor click — survives popup blockers that kill
    // window.open() invoked from a post-await callback.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    if (fileName) a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error("Failed to download document", err);
  }
};

const Field = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: any;
  children?: React.ReactNode;
}) => (
  <Box>
    <Box sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>{label}</Box>
    <Box sx={{ fontSize: 14, fontWeight: 500 }}>
      {children ?? (value ?? "—")}
    </Box>
  </Box>
);

/** Download button bound to an S3 doc key — shows "—" if no key. */
const DocDownloadButton = ({
  docKey,
  docName,
}: {
  docKey?: string;
  docName?: string;
}) => {
  const [loading, setLoading] = useState(false);
  if (!docKey) return <Box sx={{ color: "#999" }}>—</Box>;
  return (
    <Button
      text={loading ? "Opening..." : "Download"}
      onClick={async () => {
        if (loading) return;
        setLoading(true);
        try {
          await downloadDocByKey(docKey, docName);
        } finally {
          setLoading(false);
        }
      }}
    />
  );
};

const DemandNoteActions = ({
  epid,
  onDone,
}: {
  epid: string;
  onDone: () => void;
}) => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: submitDemandNote, isLoading: submitting } = useMutation(
    generateDemandNote,
    {
      onSuccess: () => onDone(),
      onError: (err: any) =>
        setError(
          err?.response?.data?.message || "Failed to upload demand note",
        ),
    },
  );

  /** Download the auto-generated demand note template (PDF with calculations). */
  const handleDownload = async () => {
    if (downloading) return;
    setError("");
    setDownloading(true);
    try {
      const res = await getDemandNotePdf(epid);
      const downloadPath = (res as any)?.data?.downloadPath;
      if (!downloadPath) {
        setError("Failed to generate the demand note template");
        return;
      }
      const url = `${API_BASE_URL}${downloadPath}`;
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      setError("Failed to download the demand note");
    } finally {
      setDownloading(false);
    }
  };

  /** Upload the signed demand note PDF (amount/due date derived server-side). */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const doc = await uploadDocument(file);
      if (!doc?.key) {
        setError("File upload failed");
        return;
      }
      submitDemandNote({
        epid,
        demandNoteDocumentKey: doc.key,
        demandNoteDocumentName: doc.name,
      } as any);
    } catch {
      setError("File upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ fontSize: 16, fontWeight: 600 }}>Demand Note</Box>
      <Box sx={{ fontSize: 13, color: "#666" }}>
        Download the auto-generated demand note (calculations are pre-filled),
        sign it, then upload the signed copy.
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          text={downloading ? "Generating..." : "Download Demand Note"}
          variant="outlined"
          onClick={handleDownload}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Button
          text={
            uploading || submitting ? "Uploading..." : "Upload Demand Note"
          }
          onClick={() => fileInputRef.current?.click()}
        />
      </Box>
      {error && <Box sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Box>}
    </Box>
  );
};

const VerifyForm = ({
  payment,
  onDone,
}: {
  payment: any;
  onDone: () => void;
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [approveRemark, setApproveRemark] = useState("");
  const [error, setError] = useState("");

  const { mutate, isLoading } = useMutation(verifyPayment, {
    onSuccess: () => onDone(),
    onError: (err: any) =>
      setError(err?.response?.data?.message || "Verification failed"),
  });

  const handleApprove = () =>
    mutate({
      epid: payment.landOwnershipEpid,
      action: "APPROVE",
      remark: approveRemark.trim() || undefined,
    });

  const handleReject = () => {
    setError("");
    if (!rejectRemark.trim())
      return setError("A rejection remark is required");
    mutate({
      epid: payment.landOwnershipEpid,
      action: "REJECT",
      rejectionRemark: rejectRemark,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 500 }}>
      <Box sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>Verify Payment</Box>
      {rejecting ? (
        <Box>
          <Box sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>
            Rejection Remark
          </Box>
          <TextField
            placeholder="Enter the reason for rejection..."
            value={rejectRemark}
            onChange={(e) => setRejectRemark(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
          />
        </Box>
      ) : (
        <Box>
          <Box sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>
            Remark (optional, saved on approve)
          </Box>
          <TextField
            placeholder="Add an optional remark..."
            value={approveRemark}
            onChange={(e) => setApproveRemark(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
      )}
      {error && <Box sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Box>}
      <Box sx={{ display: "flex", gap: 1 }}>
        {rejecting ? (
          <>
            <Button
              text="Cancel"
              variant="outlined"
              onClick={() => {
                setRejecting(false);
                setRejectRemark("");
                setError("");
              }}
            />
            <Button
              text="Confirm Reject"
              color="error"
              loading={isLoading}
              onClick={handleReject}
            />
          </>
        ) : (
          <>
            <Button
              text="Reject"
              color="error"
              variant="outlined"
              onClick={() => setRejecting(true)}
            />
            <Button
              text="Approve"
              loading={isLoading}
              onClick={handleApprove}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

const QueryThread = ({ queries }: { queries: any[] }) => {
  if (!queries?.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>Queries</Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {queries.map((q) => {
          const isAdmin = q.userType === "ADMIN";
          return (
            <Box
              key={q.id}
              sx={{
                p: 1.5,
                border: "1px solid #eee",
                borderLeft: `3px solid ${isAdmin ? "#1976d2" : "#36B1A0"}`,
                borderRadius: 1,
                background: "#fafafa",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={isAdmin ? "Admin" : "Entity"}
                  size="small"
                  sx={{
                    backgroundColor: isAdmin ? "#1976d220" : "#36B1A020",
                    color: isAdmin ? "#1976d2" : "#36B1A0",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
                <Box sx={{ fontSize: 13, fontWeight: 500 }}>
                  {q.userName ?? "—"}
                </Box>
                <Box sx={{ fontSize: 12, color: "#666", ml: "auto" }}>
                  {dateAndTimeStamp(q.createdAt, "IST")}
                </Box>
              </Box>
              <Box sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{q.query}</Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const RaiseQueryForm = ({
  epid,
  onDone,
}: {
  epid: string;
  onDone: () => void;
}) => {
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const { mutate, isLoading } = useMutation(createPaymentQuery, {
    onSuccess: () => {
      setText("");
      onDone();
    },
    onError: (err: any) =>
      setError(err?.response?.data?.message || "Failed to submit query"),
  });

  const handleSubmit = () => {
    setError("");
    if (!text.trim()) return setError("Query cannot be empty");
    mutate({ epid, query: text });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 600 }}>
      <Box sx={{ fontSize: 16, fontWeight: 600 }}>Raise Query</Box>
      <TextField
        placeholder="Type your query..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        size="small"
        fullWidth
      />
      {error && <Box sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Box>}
      <Box>
        <Button text="Submit Query" loading={isLoading} onClick={handleSubmit} />
      </Box>
    </Box>
  );
};

const PaymentManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const epid = searchParams.get("epid") ?? "";

  const { data, isFetching, refetch } = useQuery(
    ["paymentByEpid", epid],
    () => getPaymentByEpid(epid),
    { enabled: !!epid }
  );

  const payment = data?.data?.payment;
  const mou = data?.data?.mou;
  const dpr = data?.data?.dpr;
  const queries = data?.data?.queries ?? [];
  const status = payment?.status;

  return (
    <DashboardWrapper
      title="Payment Management"
      showBackBtn
      backPath={path.payment}
    >
      <Box sx={{ p: 3, background: "white", borderRadius: 1 }}>
        {!epid ? (
          <Box sx={{ color: "#d32f2f" }}>No payment selected.</Box>
        ) : isFetching || !payment ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ fontSize: 20, fontWeight: 600 }}>
                {payment.projectDetail?.name ?? "Project"}
              </Box>
              <Chip
                label={PAYMENT_STATUS_LABELS[status] ?? status}
                size="small"
                sx={{
                  backgroundColor: `${PAYMENT_STATUS_COLORS[status] ?? "#757575"}20`,
                  color: PAYMENT_STATUS_COLORS[status] ?? "#757575",
                  fontWeight: 600,
                }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 4,
                pb: 3,
                borderBottom: "1px solid #eee",
              }}
            >
              <Field label="EPID">
                <Box
                  component="span"
                  onClick={() =>
                    navigate(
                      `${path.landOwnershipDetails}?${createSearchParams({
                        id: payment.landOwnershipEpid,
                      })}`
                    )
                  }
                  sx={greenLinkSx}
                >
                  {payment.landOwnershipEpid}
                </Box>
              </Field>
              <Field
                label="Registration No."
                value={payment.projectDetail?.registrationNo}
              />
              <Field label="DPR Version Id">
                {payment.dprVersionId ? (
                  <Box
                    component="span"
                    onClick={() =>
                      navigate(
                        `${path.dprForm}?${createSearchParams({
                          landOwnershipEpid: payment.landOwnershipEpid,
                        })}`
                      )
                    }
                    sx={greenLinkSx}
                  >
                    {payment.dprVersionId}
                  </Box>
                ) : (
                  "—"
                )}
              </Field>
              <Field
                label="DPR Approved Date"
                value={
                  dpr?.approvedAt
                    ? dateAndTimeStamp(dpr.approvedAt, "IST")
                    : "—"
                }
              />
              <Field
                label="MOU Approved Date"
                value={
                  mou?.approvedAt
                    ? dateAndTimeStamp(mou.approvedAt, "IST")
                    : "—"
                }
              />
              <Field label="MOU Document">
                <DocDownloadButton
                  docKey={mou?.documentKey}
                  docName={mou?.documentName}
                />
              </Field>
              <Field label="Demand Note (Signed)">
                <DocDownloadButton
                  docKey={payment.demandNoteDocumentKey}
                  docName={payment.demandNoteDocumentName}
                />
              </Field>
              <Field label="Payment Receipt">
                <DocDownloadButton
                  docKey={payment.receiptDocumentKey}
                  docName={payment.receiptDocumentName}
                />
              </Field>
            </Box>

            {status === "DEMAND_NOTE_PENDING" && (
              <DemandNoteActions epid={epid} onDone={() => refetch()} />
            )}
            {status === "PAYMENT_VERIFICATION" && (
              <VerifyForm payment={payment} onDone={() => refetch()} />
            )}
            {status === "PAYMENT_PENDING" && (
              <Box sx={{ color: "#666" }}>
                Awaiting entity payment and receipt upload.
              </Box>
            )}
            {status === "PAYMENT_REJECTED" && (
              <Box sx={{ color: "#d32f2f" }}>
                Receipt rejected — entity needs to re-upload.
              </Box>
            )}
            {status === "PAYMENT_RECEIVED" && (
              <Box sx={{ color: "#2e7d32", fontWeight: 600 }}>
                Payment received. Eco-restoration workflow can begin.
                {payment.remark && (
                  <Box
                    sx={{
                      mt: 1,
                      fontWeight: 400,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <b>Admin remark:</b> {payment.remark}
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #eee" }}>
              <QueryThread queries={queries} />
              <RaiseQueryForm epid={epid} onDone={() => refetch()} />
            </Box>
          </>
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default PaymentManagement;
